import { useGetUrl } from '@seek/ca-myprofile-utils';
import { useTranslations } from '@vocab/react';
import {
  Text,
  Stack,
  Notice,
  Card as BraidCard,
  Divider,
} from 'braid-design-system';
import React from 'react';

import { Card } from 'src/components/Card/Card';
import { CardAction } from 'src/components/Card/CardAction/CardAction';
import { ExternalLink } from 'src/components/ExternalLink/ExternalLink';
import { FormDrawer } from 'src/components/FormDrawer/FormDrawer';
import {
  LoadingCardItem,
  LoadingItem,
} from 'src/components/LoadingBlock/LoadingBlock';
import { RESUME } from 'src/components/Router';
import { EDIT } from 'src/consts';
import { useAppConfig } from 'src/modules/appConfig';
import { RESUME_FORM } from 'src/reducers/forms';
import { VisibilityLevel } from 'src/types/graphql';

import { CreateResumeCTA } from '../../CreateResume/CreateResumeCTA/CreateResumeCTA';
import { useGetProfileVisibilityLevel } from '../../ProfileVisibility/data';
import { EditResume } from '../EditResume/EditResume';
import { ResumeDetailItem } from '../EditResume/ManageResumes/ResumeDetailItem/ResumeDetailItem';
import { ProfileVisibilityNudgeLink } from '../ProfileVisibilityNudgeLink/ProfileVisibilityNudgeLink';
import { useResumes } from '../data';

import translations from './.vocab';

export const ReadResume = () => {
  const { t } = useTranslations(translations);
  const { zone, language, isUnifiedSite } = useAppConfig();
  const { resumes, defaultResumeId, loading, error } = useResumes();
  const resumeCount = resumes.length;
  const otherResumeCount = resumeCount - 1;
  const isEmpty = resumeCount === 0;
  const getUrl = useGetUrl(zone, language, isUnifiedSite);

  const { profileVisibilityLevel } = useGetProfileVisibilityLevel();

  const defaultResumeExists =
    resumes.findIndex((resume) => resume.id === defaultResumeId) !== -1;

  const isLoading = Boolean(loading || error);

  const PromoteAddResumeText = () => (
    <Text data={{ automation: 'promote-add-resume-text' }}>
      {t(
        'Upload a resumé for easy applying and access no matter where you are.',
      )}
    </Text>
  );

  const ResumeInformationDetails = () => {
    const isStandardProfileVisibility =
      profileVisibilityLevel === VisibilityLevel.Standard;

    return isStandardProfileVisibility ? (
      <Text>
        {t('This resumé is visible to employers.', {
          ExternalLink: (v) => (
            <ExternalLink
              target="_blank"
              rel="noopener noreferrer"
              href={getUrl('profile-privacy')}
              weight="weak"
              hitArea="large"
            >
              {v}
            </ExternalLink>
          ),
        })}
      </Text>
    ) : (
      <Text>
        {t(
          'To make your resumé visible to employers change your visibility setting to standard.',
          {
            ExternalLink: (v) => (
              <ExternalLink
                target="_blank"
                rel="noopener noreferrer"
                href={getUrl('profile-privacy')}
                weight="weak"
                hitArea="large"
              >
                {v}
              </ExternalLink>
            ),
            ProfileVisibilityNudgeLink: (v) => (
              <ProfileVisibilityNudgeLink
                text={v.toString()}
                analytics={{ origin: 'manage resumes' }}
              />
            ),
          },
        )}
      </Text>
    );
  };

  const DefaultResumeCard = () => {
    const defaultResume = resumes.find(
      (resume) => resume.id === defaultResumeId,
    );

    return defaultResume ? (
      <Stack space="large">
        <ResumeDetailItem
          {...defaultResume}
          highlightResume={false}
          readOnly={true}
        />
        <ResumeInformationDetails />
      </Stack>
    ) : null;
  };

  const SelectDefaultResumeNotice = () => (
    <Notice tone="critical">
      <Text>{t('Select a default resumé.')}</Text>
    </Notice>
  );

  const ResumeCardAction = () => {
    const editLinkText = isEmpty
      ? t('Add resumé')
      : t('Add or manage resumés', { resumeCount: String(resumeCount) });

    return (
      <Stack space="small">
        <CardAction
          to={RESUME}
          mode={EDIT}
          data={{ automation: 'resume-edit-link' }}
          actionText={editLinkText}
          isEmpty={isEmpty}
          analytics={{
            currentPageSectionState: isEmpty ? 'add' : 'edit',
          }}
        />
        {defaultResumeExists && otherResumeCount > 0 ? (
          <Text tone="secondary">
            {t('You have n other saved resumés.', {
              otherResumeCount,
            })}
          </Text>
        ) : null}
      </Stack>
    );
  };

  return (
    <>
      <Card
        name={RESUME}
        title={t('Resumé')}
        loading={isLoading}
        loader={
          <Stack space="large">
            <LoadingCardItem />
            <LoadingItem type="text" size="standard" width="medium" />
            <LoadingItem type="button" />
          </Stack>
        }
      >
        <BraidCard>
          <Stack space="large">
            <Stack space="large">
              {isEmpty ? (
                <PromoteAddResumeText />
              ) : (
                <>
                  {defaultResumeExists ? (
                    <DefaultResumeCard />
                  ) : (
                    <SelectDefaultResumeNotice />
                  )}
                </>
              )}
              <ResumeCardAction />
            </Stack>
            <Divider />
            <CreateResumeCTA />
          </Stack>
        </BraidCard>
      </Card>
      <FormDrawer
        title={t('Add resumé')}
        component={EditResume}
        card={RESUME}
        formName={RESUME_FORM}
      />
    </>
  );
};
